/**
 * discussions/{id} + discussions/{id}/replies/{id} — the Studio Board.
 *
 * The "Slack #general" of the Hub. Distinct from the participant-scoped
 * /messages threads — the board is implicitly readable by all active
 * staff, no membership management. Each post has an optional
 * ackRequired flag that keeps it pinned on the "What needs you" pane
 * until each staff member either acknowledges OR dismisses.
 *
 * Replies form a flat thread under each post (no nested threading; if
 * a sub-conversation needs to happen, start a new post or take it to
 * DMs).
 */

import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import { useEffect, useState } from 'react';
import { discussionBelongsInPersonalInbox } from './personalDiscussionAudience';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './auth-context';
import { useBoardMembershipEffectiveMember } from './boardMembership';
import {
  STAFF_ROLES,
  STUDIO_GROUPS,
  isDancerLoginUserDoc,
  useUserDoc,
  type DiscussionAudienceGroup,
  type StudioGroup,
  type UserRole,
} from './users';
import { teacherDisplayName } from './teacherTitle.ts';
import { notifyAudience, notifyDiscussionEvent, notifyUsers } from './notifications';
import type { MessageAttachment } from './threads';
import { functionHeaders } from './functionSecurity';
import type { User } from 'firebase/auth';
import { discussionAudienceIncludesUser, discussionReachesUser } from './discussionAudience';
import { useReviveAfterError } from './listenerRevival';

export interface DiscussionDoc {
  id: string;
  title: string;
  body: string;
  /** Photos, videos, and safe files shared with this conversation. */
  attachments?: MessageAttachment[];
  authorUid: string;
  /** Snapshot label so group members never need another family's private user doc. */
  authorName?: string;
  /**
   * Empty = visible to all active staff. Non-empty = visible only to
   * users whose role is in the list. Director-only by convention is
   * `['director']`; "executives + admin" is `['company', 'foh']`; etc.
   * Director can always read everything regardless.
   */
  audienceRoles: UserRole[];
  /**
   * Named audiences that can see this post IN ADDITION to audienceRoles.
   * 'interns' is a private membership read off the user doc; 'jrStaff' is the
   * Jr Staff board's derived roll, which is how the parents standing in for
   * Jr Staff reach an announcement addressed to Jr Staff.
   */
  audienceGroups?: DiscussionAudienceGroup[];
  /** Reserved private-board lane. Absent for normal Studio Board posts. */
  boardKey?: 'executiveTeachers';
  ackRequired: boolean;
  acks: Record<string, Timestamp | FieldValue>;
  dismissedBy: Record<string, Timestamp | FieldValue>;
  replyCount: number;
  lastReplyAt: Timestamp | null;
  /**
   * Per-user "high-water mark" of replies they've seen. Keyed by uid →
   * the replyCount value at the moment they last expanded this post.
   * New-reply badge = replyCount - (seenReplyCount[me] ?? 0), clamped
   * to >= 0. Lives on the parent doc (not the replies subcollection) so
   * the badge renders without loading replies. Optional for back-compat
   * with posts created before this field existed (absence reads as 0).
   */
  seenReplyCount?: Record<string, number>;
  /** Set when the author edits the title/body after posting. */
  editedAt?: Timestamp | FieldValue;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
}

export interface ReplyDoc {
  id: string;
  authorUid: string;
  /** Snapshot label for readers without staff-directory access. */
  authorName?: string;
  text: string;
  /** Photos, videos, and safe files shared with this reply. */
  attachments?: MessageAttachment[];
  /** People specifically called out from this reply. */
  mentions?: string[];
  /** Staff role groups specifically called out from this reply. */
  mentionRoles?: UserRole[];
  /** Set when the author edits the reply after posting. */
  editedAt?: Timestamp | FieldValue;
  createdAt: Timestamp | FieldValue;
}

export interface UseDiscussionsResult {
  loading: boolean;
  discussions: DiscussionDoc[];
  error: string | null;
  /**
   * True once at least one underlying query slice actually hit maxDocs (the
   * same "did the raw read hit its cap" signal useArchivedBulletins exposes
   * for bulletins). An older discussion beyond that slice could exist and
   * never surface in a search that assumes this list is complete.
   */
  capped: boolean;
}

/**
 * All discussions. Sorted by lastReplyAt desc (then createdAt desc as
 * fallback so brand-new posts with no replies still surface at the
 * top).
 */
/**
 * Listener caps (perf round, audit F3). The Director branch was a bare
 * unbounded listener over every discussion ever posted. Newest-100 by
 * createdAt covers the attention counts and inbox rows; /board passes
 * BOARD_HISTORY_CAP for deep history. KNOWN TRUNCATION: the slices order
 * by createdAt, so an OLD post revived by a new reply can fall outside
 * the newest-N window even though the client sorts by lastReplyAt - at
 * the default cap that only bites once a surface is 100+ posts deep.
 * limit() needs no new index.
 */
export const BOARD_RECENT_CAP = 100;
export const BOARD_HISTORY_CAP = 500;

function trustedInternAuthorName(userDoc: ReturnType<typeof useUserDoc>['realUserDoc']): string | null {
  if (userDoc?.role === 'director') return 'Studio Director';
  const approved = userDoc?.internName?.trim();
  return approved ? approved.slice(0, 120) : null;
}

/**
 * `onJrStaffRoll` says this caller is on jrStaffBoardMembership/current. It
 * adds exactly ONE provable query slice - announcements whose audienceGroups
 * contain 'jrStaff' - which is the slice shared/firestore.rules'
 * jrStaffAudienceCoversRollCaller allows a guardian standing in for a Jr
 * Staff dancer.
 *
 * RUN FOR STAFF CALLERS TOO (Finding 1, PR #807 review). Roll membership and
 * staff role are independent, so a staff-role account ALSO on the roll (a
 * stand-in whose own role is 'foh', 'company', or 'manager' - the announcement
 * does not name it) needs this slice exactly like a family stand-in does: her
 * role queries below cover only her own role and the empty-audience shape,
 * neither of which is a Jr-Staff-only post. This is one extra listener for a
 * staff caller who is on the roll, never for one who is not (onJrStaffRoll is
 * false for her).
 *
 * `personal` asks for the announcements addressed to THIS person rather than
 * everything she is allowed to read. It changes one thing: a Director gets the
 * same per-audience slices every other staff role reads (audience empty,
 * audience names her role, her named groups) instead of the studio-wide
 * newest-N list. That studio-wide list is the one place the two answers
 * differ, and it is where Bobbie's announcements went missing (2026-09-29):
 * the newest 100 posts for ANY audience were read and then filtered down to
 * hers, so 100 posts to other roles pushed an unanswered one of hers out of
 * Messages and out of the home count with nothing on screen saying so. Search
 * and the Interns board still pass false: a Director moderating or searching
 * genuinely needs every post. Every slice here is a shape other staff roles
 * already run, so no new index, and the rules answer the Director first
 * (`allow read: if isDirector() || ...` on discussions).
 */
export function useDiscussions(
  enabled = true,
  maxDocs = BOARD_RECENT_CAP,
  onJrStaffRoll = false,
  personal = false
): UseDiscussionsResult {
  const { userDoc, loading: userLoading } = useUserDoc();
  const groupKey = (isDancerLoginUserDoc(userDoc) ? [] : (userDoc?.groups ?? []))
    .filter((group): group is StudioGroup => STUDIO_GROUPS.includes(group as StudioGroup))
    .sort()
    .join('|');
  const [discussions, setDiscussions] = useState<DiscussionDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // True once at least one of the parallel query slices below actually hit
  // maxDocs on its own raw read - tracked separately from `discussions`
  // (the merged, de-duplicated result) the same way useClassPostInbox and
  // useArchivedBulletins track their own cap off the raw snapshot.
  const [capped, setCapped] = useState(false);
  // A failed slice's listener is dead; rebuild the set when the person comes
  // back to the app or the network returns (lib/listenerRevival.ts). The
  // rebuild below clears the error before it resubscribes.
  const revival = useReviveAfterError(error !== null);

  useEffect(() => {
    if (!enabled) {
      setDiscussions([]);
      setError(null);
      setLoading(false);
      setCapped(false);
      return;
    }
    // Unconditionally back to true on the enabled path. The disabled branch
    // above leaves loading false, and the consumers gate on the SAME user doc
    // that settles userLoading here (Messages' staff inbox, Search), so
    // userLoading is already false when enabled flips on and its branch below
    // cannot cover the flip. Without this the inbox settles with no board
    // announcements, ack-required ones included, and then they pop in.
    setLoading(true);
    if (userLoading) {
      return;
    }
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    if (!userDoc) {
      setDiscussions([]);
      setLoading(false);
      setCapped(false);
      return;
    }

    // Security rules gate board reads per document by audienceRoles, and
    // Firestore can only allow a LIST query it can prove from the query's
    // own filters. A bare orderBy over the whole collection is unprovable
    // for everyone but the Director, so non-Director staff subscribe to two
    // provable slices instead: everyone-posts (audienceRoles == []) and
    // posts that name their own role. Same fix pattern as bulletins and
    // newsletters.
    const base = collection(db, 'discussions');
    const groups = groupKey ? (groupKey.split('|') as StudioGroup[]) : [];
    const roleQueries = STAFF_ROLES.includes(userDoc.role)
      ? [
            query(
              base,
              where('audienceRoles', '==', []),
              orderBy('createdAt', 'desc'),
              limit(maxDocs + 1)
            ),
            query(
              base,
              where('audienceRoles', 'array-contains', userDoc.role),
              orderBy('createdAt', 'desc'),
              limit(maxDocs + 1)
            ),
        ]
      : [];
    const groupQueries = groups.map((group) =>
      query(
        base,
        where('audienceGroups', 'array-contains', group),
        orderBy('createdAt', 'desc'),
        limit(maxDocs + 1)
      )
    );
    // ALWAYS run this slice when onJrStaffRoll is true, staff role or not
    // (Finding 1, PR #807 review). Roll membership and staff role are
    // independent: a roll or overlay member who ALSO holds a staff role the
    // announcement does not name (the documented FOH, company, or manager
    // stand-in - see jrStaffAudienceCoversRollCaller in
    // shared/firestore.rules) has roleQueries answer only their own role and
    // the empty-audience shape, neither of which covers a Jr-Staff-only post
    // like audienceRoles ['ensemble']. The rules permit the read and notify
    // sends the email; without this slice the post never reaches byId, so
    // the family gets the email and cannot find it in the app.
    const jrStaffRollQueries = onJrStaffRoll
      ? [
          query(
            base,
            where('audienceGroups', 'array-contains', 'jrStaff'),
            orderBy('createdAt', 'desc'),
            limit(maxDocs + 1)
          ),
        ]
      : [];
    const queries =
      userDoc.role === 'director' && !personal
        ? [query(base, orderBy('createdAt', 'desc'), limit(maxDocs + 1))]
        : [...roleQueries, ...groupQueries, ...jrStaffRollQueries];

    if (queries.length === 0) {
      setDiscussions([]);
      setError(null);
      setLoading(false);
      setCapped(false);
      return;
    }
    // CLEAR A STALE error ON REBUILD (Codex P2b, PR #807 round 17): this
    // effect reruns whenever userDoc.role/groupKey/onJrStaffRoll changes -
    // a membership flip after a removal is exactly such a rebuild, tearing
    // down the OLD listeners (including one that may have errored) and
    // opening a brand NEW set for the CURRENT `queries`. Round 16 already
    // makes a fresh slice's own onSnapshot error drop only ITS OWN docs and
    // republish - that part was never the bug. The bug is `error` itself:
    // a single shared string for the whole hook, set once by any slice's
    // error and never cleared on this rebuild path (only the disabled and
    // no-queries branches above ever call setError(null)), so a permission-
    // denied banner from a slice that no longer even EXISTS in the new
    // `queries` array kept showing over a mount whose every current slice
    // is healthy. Cleared here, synchronously, BEFORE any of the fresh
    // onSnapshot calls below can run their own callbacks (Firestore never
    // invokes onSnapshot's callbacks synchronously from the subscribe call
    // itself) - so a slice that is STILL in the current set and genuinely
    // errors keeps reporting exactly as before; only a rebuild with no
    // fresh error at all stays clear.
    setError(null);

    // PER-SLICE RESULT SETS, not one shared map a slice can evict from
    // (Finding B, PR #807 round 2). A staff account on the Jr Staff roll
    // commonly has the SAME announcement land in both jrStaffRollQueries
    // and a roleQueries slice - the one shape finding 1 introduced overlap
    // for. A shared byId map let either slice's own eviction (a later post
    // pushing an older one out of that slice's maxDocs window) delete the
    // id out from under the OTHER slice that still holds it, so a readable
    // announcement vanished from Messages until that other listener
    // happened to fire again. Each slice now owns its own map; merging
    // happens fresh on every publish, so a doc survives as long as ANY
    // slice still reports it.
    const sliceDocs: Map<string, DiscussionDoc>[] = queries.map(() => new Map());
    const seeded = queries.map(() => false);
    const cappedFlags = queries.map(() => false);
    const publish = () => {
      if (!seeded.every(Boolean)) return;
      const byId = new Map<string, DiscussionDoc>();
      for (const slice of sliceDocs) {
        for (const [id, discussion] of slice) byId.set(id, discussion);
      }
      const docs = Array.from(byId.values());
      docs.sort((a, b) => {
        const aMs =
          (a.lastReplyAt as Timestamp | null)?.toMillis?.() ??
          (a.createdAt as Timestamp | null)?.toMillis?.() ??
          0;
        const bMs =
          (b.lastReplyAt as Timestamp | null)?.toMillis?.() ??
          (b.createdAt as Timestamp | null)?.toMillis?.() ??
          0;
        return bMs - aMs;
      });
      setDiscussions(docs);
      setCapped(cappedFlags.some(Boolean));
      setLoading(false);
    };

    const unsubs = queries.map((q, i) =>
      onSnapshot(
        q,
        (snap) => {
          const slice = new Map<string, DiscussionDoc>();
          for (const d of snap.docs) {
            slice.set(d.id, { id: d.id, ...(d.data() as Omit<DiscussionDoc, 'id'>) });
          }
          sliceDocs[i] = slice;
          // ONE MORE than the window is asked for, so a slice holding
          // exactly maxDocs posts reads as complete; only MORE than that
          // means something older sits past the edge.
          cappedFlags[i] = snap.docs.length > maxDocs;
          seeded[i] = true;
          publish();
        },
        (err) => {
          // DROP THIS SLICE'S OWN DOCS ON ERROR (Codex, PR #807 round 16):
          // leaving sliceDocs[i] holding whatever it last held let a slice
          // whose own legitimacy can be revoked mid-session (the Jr Staff
          // roll slice, tied to a live roll/overlay membership, not to the
          // userDoc.role/groupKey/onJrStaffRoll deps this whole effect
          // rebuilds from) go on contributing its now-stale rows to every
          // FUTURE merge, since only a fresh success from THIS slice would
          // otherwise ever overwrite sliceDocs[i] again - and an errored
          // listener never calls its success callback again on its own.
          // Dropping only this slice's own map (never another's) and
          // re-publishing immediately means the merge reflects the drop on
          // THIS event, not whenever some unrelated slice next happens to
          // fire. Also marks this slice seeded (even though empty) so a
          // slice whose very FIRST event is an error does not block
          // publish() forever for every OTHER slice that did succeed -
          // `seeded.every(Boolean)` could never become true otherwise.
          sliceDocs[i] = new Map();
          cappedFlags[i] = false;
          seeded[i] = true;
          setError(err.message);
          setLoading(false);
          publish();
        }
      )
    );
    return () => unsubs.forEach((unsub) => unsub());
  }, [enabled, userDoc?.role, userLoading, maxDocs, groupKey, onJrStaffRoll, personal, revival]);

  return { loading, discussions, error, capped };
}

/**
 * Personal staff inbox view. A Director's broad read access remains available
 * to Search and administrative surfaces, but Messages and its attention count
 * only include announcements actually addressed to their role or named group.
 * They are also READ that way (`personal` on useDiscussions), so the window
 * each slice keeps is a window of her own announcements, and `capped` says
 * when one of those slices came back full.
 *
 * `jrStaffBoardRemoved` (PR #807 round 8, Codex P1-a) is the caller's own
 * Manage members overlay removedUids entry for the Jr Staff board - pass
 * true only for a caller this session has confirmed was actually removed,
 * never inferred. Threaded straight through to discussionReachesUser, whose
 * own comment covers why this cannot fold into onJrStaffRoll. Defaults to
 * false (unremoved) so every other existing caller keeps today's behaviour.
 */
export function usePersonalDiscussions(
  enabled = true,
  maxDocs = BOARD_RECENT_CAP,
  onJrStaffRoll = false,
  jrStaffBoardRemoved = false
): UseDiscussionsResult {
  const { userDoc, realUserDoc } = useUserDoc();
  const result = useDiscussions(enabled, maxDocs, onJrStaffRoll, true);
  if (!userDoc || !realUserDoc || !enabled) return result;
  const groups = isDancerLoginUserDoc(userDoc) ? [] : (userDoc.groups ?? []);
  return {
    ...result,
    // REACHES, not participates: a parent standing in for a Jr Staff dancer
    // gets the announcement on their list without becoming one of the people
    // its acknowledgement roster is waiting on. See discussionReachesUser.
    discussions: result.discussions.filter((discussion) =>
      discussionBelongsInPersonalInbox(discussion, userDoc.role, groups, onJrStaffRoll)
      && discussionReachesUser(discussion, realUserDoc, onJrStaffRoll, jrStaffBoardRemoved)
    ),
  };
}

export interface UseRepliesResult {
  loading: boolean;
  replies: ReplyDoc[];
}

export function useReplies(discussionId: string | undefined): UseRepliesResult {
  const [replies, setReplies] = useState<ReplyDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db || !discussionId) {
      setReplies([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'discussions', discussionId, 'replies'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setReplies(
          snap.docs.map(
            (d) => ({ id: d.id, ...(d.data() as Omit<ReplyDoc, 'id'>) })
          )
        );
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );
    return () => unsub();
  }, [discussionId]);

  return { loading, replies };
}

/**
 * Outstanding discussions for the signed-in user. "Outstanding" =
 * ackRequired is true AND I haven't yet acked or dismissed. Used by
 * the StaffAttentionCard count, the Studio Board nav badges, and the
 * Messages unified inbox.
 *
 * Pass `enabled = false` for viewers who should never listen to the
 * discussions collection (family, pending/rejected accounts) so a
 * single component tree doesn't open a listener that rules will deny.
 */
export function useMyOutstandingDiscussions(enabled = true): {
  loading: boolean;
  count: number;
  /**
   * Forwarded from useDiscussions (added for the honesty sweep): that
   * hook already tracks this, it just was not being passed through, so a
   * consumer reading only `count` here could not tell a genuinely-zero
   * result from a failed listen that also settles on count=0. Non-null
   * only when the underlying discussions listener itself failed.
   */
  error: string | null;
  /**
   * True when one of the announcement slices came back with MORE than
   * BOARD_RECENT_CAP posts, so an older unanswered one could sit past its
   * edge. Forwarded from useDiscussions, which always tracked it; this hook
   * used to drop it, which is how a full window read as "All clear, nice" on
   * the home card. It is not a failure: only the home row's zero line uses
   * it (see WaitingCountState in staffWaitingOnYouCore.ts), because a slice
   * fills up with FYI posts, answered posts and board posts and stays full.
   */
  capped: boolean;
  /**
   * The announcements on her list (the same personal list Messages draws
   * its staff announcement rows from), and which of them are addressed to
   * her (discussionAudienceIncludesUser). B17: the Waiting on you count
   * reads these to count a row Messages shows as unread for new replies,
   * not only for an acknowledgement (lib/messagesUnreadCore.ts).
   */
  discussions: DiscussionDoc[];
  participatingIds: ReadonlySet<string>;
} {
  const { user } = useAuth();
  const { realUserDoc, loading: userLoading } = useUserDoc();
  // `jrStaffBoardRemoved` (PR #807 round 9, Codex P2): this viewer's own
  // Jr Staff board Manage-members removal, read straight off the overlay
  // and threaded into usePersonalDiscussions below. Round 8 fixed
  // discussionReachesUser so a removed member's row disappears from the
  // Messages list; this hook feeds the SEPARATE outstanding-action count
  // (NavDrawer's badge, the home cards, staffWaitingOnYou) from its own
  // uncomposed usePersonalDiscussions(enabled) call, which never threaded
  // onJrStaffRoll or removal through - so a removed member kept being
  // counted for an ack-required Jr Staff announcement they could no longer
  // even see, a badge with no row left to clear it. `autoIn` is passed
  // false on purpose: `removed` reads straight off the overlay's own
  // removedUids, independent of autoIn (see
  // useBoardMembershipEffectiveMember's own comment in boardMembership.ts)
  // - this hook has no use for roll-widened `member` (a stand-in guardian
  // never acks, so never belongs in an outstanding-ACTION count), only for
  // whether removal specifically happened, so it does not duplicate the
  // roll-membership read Messages.tsx already owns for its own door.
  //
  // This is the COUNT agreeing with what the list shows, not a
  // participation grant: discussionAudienceIncludesUser below (the ack
  // eligibility / participation predicate) is deliberately untouched, per
  // the same queue task 35 boundary round 8 documented. The document
  // simply is not in `discussions` any more once removed, courtesy of
  // discussionReachesUser's removal fence - which runs regardless of
  // onJrStaffRoll, so passing it through here needs no roll-widening.
  const {
    settled: jrStaffBoardOverlaySettled,
    removed: jrStaffBoardRemovedRaw,
    fromCache: jrStaffBoardOverlayFromCache,
    error: jrStaffBoardOverlayErrored,
  } = useBoardMembershipEffectiveMember(
    'jrStaffBoard',
    enabled ? user?.uid : undefined,
    false
  );
  // FAIL CLOSED WHILE THE OVERLAY IS UNSETTLED (Codex P1, PR #807 round 13,
  // same fix as Messages.tsx's own onJrStaffRoll door - see its comment for
  // the full derivation). A cached role-query snapshot can render before
  // this overlay's first snapshot lands, so `removed` defaulting to false
  // against the not-yet-loaded PURE_EMPTY_OVERLAY would count a removed
  // member in this outstanding-action badge for that window, then drop her
  // the instant the overlay settles - the exact badge-flicker round 9 fixed
  // discussionReachesUser to prevent, just not for the unsettled instant.
  //
  // NOR IS A CACHED SNAPSHOT A CONFIRMED ONE (Codex P1, PR #807 round 14,
  // the direct follow-on - see Messages.tsx's own identical fix for the
  // full derivation). The overlay's first snapshot can itself be served
  // from local cache and predate a removal that already happened
  // server-side, so `removedRaw` off that cached data is not yet a proven
  // answer either. Folded in the same way: unconfirmed reads as removed.
  //
  // NOR IS AN ERROR THE SAME AS A CONFIRMED CLEAR (Codex P2a, PR #807 round
  // 15, the direct follow-on - see Messages.tsx's own identical fix for the
  // full derivation and boardMembership.ts's own comment for why an error
  // AFTER a confirmed answer already landed needs no extra term here: the
  // hook itself preserves that confirmed overlay across the error.
  const jrStaffBoardRemoved =
    !jrStaffBoardOverlaySettled
    || jrStaffBoardOverlayFromCache
    || jrStaffBoardOverlayErrored
    || jrStaffBoardRemovedRaw;
  const { loading, discussions, error, capped } = usePersonalDiscussions(
    enabled, BOARD_RECENT_CAP, false, jrStaffBoardRemoved
  );
  const participatingIds = new Set(
    realUserDoc
      ? discussions.filter((d) => discussionAudienceIncludesUser(d, realUserDoc)).map((d) => d.id)
      : []
  );
  if (!user || !enabled || !realUserDoc) {
    return {
      loading: loading || userLoading,
      count: 0,
      error,
      capped: false,
      discussions: [],
      participatingIds: new Set<string>(),
    };
  }
  const me = user.uid;
  const count = discussions.filter(
    (d) =>
      d.ackRequired &&
      d.authorUid !== me &&
      discussionAudienceIncludesUser(d, realUserDoc) &&
      !(d.acks && d.acks[me]) &&
      !(d.dismissedBy && d.dismissedBy[me])
  ).length;
  return { loading: loading || userLoading, count, error, capped, discussions, participatingIds };
}

export interface NewDiscussionInput {
  id?: string;
  title: string;
  body: string;
  attachments?: MessageAttachment[];
  ackRequired: boolean;
  audienceRoles: UserRole[];
  audienceGroups?: DiscussionAudienceGroup[];
  boardKey?: 'executiveTeachers';
}

interface DiscussionWriteResponse {
  ok?: boolean;
  error?: string;
  id?: string;
  created?: boolean;
  parentTitle?: string;
  parentGroups?: DiscussionAudienceGroup[];
  parentBoardKey?: 'executiveTeachers';
}

async function saveAttachedDiscussionViaServer(
  user: User,
  body: Record<string, unknown>
): Promise<DiscussionWriteResponse> {
  const response = await fetch('/.netlify/functions/discussionWrite', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(user),
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({})) as DiscussionWriteResponse;
  if (!response.ok || payload.error) {
    const messages: Record<string, string> = {
      not_allowed: 'Your current Hub access does not allow this message.',
      discussion_not_found: 'That conversation is no longer available.',
      attachment_not_ready: 'One attachment is no longer ready. Choose it again and retry.',
      attachment_already_used: 'One attachment was already sent somewhere else. Choose it again.',
      conflict: 'That message changed in another tab. Refresh and try again.',
      server_not_configured: 'The studio message server is not configured yet.',
    };
    throw new Error(messages[payload.error ?? ''] ?? 'That message could not be sent.');
  }
  return payload;
}

export function useCreateDiscussion() {
  const { user } = useAuth();
  const { realUserDoc } = useUserDoc();
  return async (input: NewDiscussionInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    if (!input.title.trim()) throw new Error('Title is required.');
    const audienceGroups = Array.from(
      new Set(
        (input.audienceGroups ?? []).filter(
          (group) => group === 'jrStaff'
            || STUDIO_GROUPS.includes(group as StudioGroup)
        )
      )
    );
    const authorName = audienceGroups.includes('interns')
      ? trustedInternAuthorName(realUserDoc)
      : realUserDoc?.displayName || null;
    if (audienceGroups.includes('interns') && !authorName) {
      throw new Error('Your Intern membership is not ready yet. Ask the Director to remove and add you again.');
    }
    const ref = input.id
      ? doc(db, 'discussions', input.id)
      : doc(collection(db, 'discussions'));
    const attachments = input.attachments ?? [];
    await saveAttachedDiscussionViaServer(user, {
      action: 'create',
      discussionId: ref.id,
      input: {
        title: input.title.trim(),
        body: input.body.trim(),
        attachmentReservationIds: attachments.map((attachment) => attachment.id),
        ackRequired: input.ackRequired,
        audienceRoles: input.audienceRoles,
        audienceGroups,
        ...(input.boardKey ? { boardKey: input.boardKey } : {}),
      },
    });

    // Best-effort alert. The private Intern lane resolves current membership
    // on the server; ordinary staff broadcasts preserve their existing path.
    try {
      // A Jr Staff announcement takes the SERVER-DERIVED lane, for the same
      // reason the two private boards do: eleven of its people are parents
      // standing in for Jr Staff, and this browser cannot read a family
      // account to find them (notifyAudience's users read returns active
      // STAFF only for anyone below Director). The server resolves the
      // announcement's role audience union the Jr Staff board's derived roll
      // and proves every recipient from its own reads.
      if (audienceGroups.includes('jrStaff')
        || audienceGroups.includes('interns')
        || input.boardKey === 'executiveTeachers') {
        await notifyDiscussionEvent(ref.id);
      } else {
        const roles = input.audienceRoles.length ? input.audienceRoles : STAFF_ROLES;
        await notifyAudience(roles, {
          kind: 'board',
          title: `Staff announcement: ${input.title.trim()}`,
          body: plainMessageText(input.body).trim().slice(0, 140),
          // Announcements live in the merged Messages list; the row there
          // carries the matching #board-{id} anchor. (Old /board links still
          // redirect there too.)
          link: `/messages#board-${ref.id}`,
        }, { staffScopedSource: { kind: 'discussion', id: ref.id } });
      }
    } catch (e) {
      /* A fan-out that was DENIED is not "best effort", it is a send that did
         not happen. This was a bare `catch { // best-effort }`, which is how a
         Jr Staff announcement could reach nobody in total silence (see
         notifyAudience's own note on the users LIST denial that caused it).

         IT STILL DOES NOT THROW, deliberately. The announcement is already
         committed on the line above, and three callers - NewMessage, Interns
         and the Assistant - treat a throw here as "the send failed". Throwing
         would report a failure on a post that really did save, and the
         Assistant would be free to say it again and duplicate it. Losing the
         ping is bad; losing the post, or posting it twice, is worse.

         So it is logged with the discussion id rather than dropped. Telling
         the SENDER that her post saved but her ping did not is the right end
         state and is a real product decision (what the composer says, whether
         it offers a retry), recorded in the queue rather than guessed at here. */
      console.error('board: the staff announcement saved but notified nobody', ref.id, e);
    }
    return ref.id;
  };
}

/**
 * Count of replies the signed-in user hasn't seen yet on a given
 * discussion. Cheap: reads only the denormalized fields already on the
 * parent doc — no subcollection fetch. The author's own replies still
 * count as "seen" implicitly because expanding (which they do to reply)
 * bumps their high-water mark.
 */
export function unseenReplyCount(
  d: DiscussionDoc,
  uid: string | undefined
): number {
  if (!uid) return 0;
  const total = d.replyCount ?? 0;
  const seen = (d.seenReplyCount && d.seenReplyCount[uid]) ?? 0;
  return Math.max(0, total - seen);
}

/**
 * Mark all current replies on a discussion as seen for the signed-in
 * user by stamping seenReplyCount.{uid} = the post's current replyCount.
 * Called when a user expands the replies. Idempotent — re-stamping the
 * same value is a harmless no-op write, but we skip it when already
 * caught up to avoid needless writes.
 */
export function useMarkRepliesSeen() {
  const { user } = useAuth();
  return async (d: DiscussionDoc): Promise<void> => {
    if (!db || !user) return;
    if (unseenReplyCount(d, user.uid) === 0) return;
    await updateDoc(doc(db, 'discussions', d.id), {
      [`seenReplyCount.${user.uid}`]: d.replyCount ?? 0,
      updatedAt: serverTimestamp(),
    });
  };
}

export function useAckDiscussion() {
  const { user } = useAuth();
  return async (id: string): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await updateDoc(doc(db, 'discussions', id), {
      [`acks.${user.uid}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };
}

export function useDismissDiscussion() {
  const { user } = useAuth();
  return async (id: string): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await updateDoc(doc(db, 'discussions', id), {
      [`dismissedBy.${user.uid}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };
}

/**
 * Post a reply + update the parent discussion's denormalized
 * lastReplyAt + replyCount in the same transaction.
 */
export interface ReplyToDiscussionOptions {
  replyId?: string;
  attachments?: MessageAttachment[];
  mentions?: string[];
  mentionRoles?: UserRole[];
}

async function resolveMentionRoleUids(roles: UserRole[]): Promise<string[]> {
  if (!db || roles.length === 0) return [];
  const snap = await getDocs(
    query(collection(db, 'users'), where('status', '==', 'active'), where('role', 'in', STAFF_ROLES))
  );
  return snap.docs
    .map((d) => d.data() as { uid: string; role: UserRole })
    .filter((u) => roles.includes(u.role))
    .map((u) => u.uid);
}

export function useReplyToDiscussion() {
  const { user } = useAuth();
  const { realUserDoc } = useUserDoc();
  return async (
    discussionId: string,
    text: string,
    options: ReplyToDiscussionOptions = {}
  ): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const trimmed = text.trim();
    const attachments = options.attachments ?? [];
    if (!trimmed && attachments.length === 0) throw new Error('Empty reply.');
    const replyRef = options.replyId
      ? doc(db, 'discussions', discussionId, 'replies', options.replyId)
      : doc(collection(db, 'discussions', discussionId, 'replies'));
    const mentions = Array.from(new Set(options.mentions ?? [])).filter(
      (uid) => uid && uid !== user.uid
    );
    const mentionRoles = Array.from(new Set(options.mentionRoles ?? [])).filter(
      (role): role is UserRole => STAFF_ROLES.includes(role as UserRole)
    );
    let parentTitle = 'Staff announcement';
    let parentGroups: DiscussionAudienceGroup[] = [];
    let parentBoardKey: 'executiveTeachers' | undefined;
    const result = await saveAttachedDiscussionViaServer(user, {
      action: 'reply',
      discussionId,
      replyId: replyRef.id,
      input: {
        text: trimmed,
        attachmentReservationIds: attachments.map((attachment) => attachment.id),
        mentions,
        mentionRoles,
      },
    });
    parentTitle = result.parentTitle || 'Staff announcement';
    parentGroups = result.parentGroups ?? [];
    parentBoardKey = result.parentBoardKey;

    try {
      if (parentGroups.includes('interns') || parentBoardKey === 'executiveTeachers') {
        await notifyDiscussionEvent(discussionId, replyRef.id);
      } else {
        const roleUids = await resolveMentionRoleUids(mentionRoles);
        const recipients = Array.from(new Set([...mentions, ...roleUids])).filter(
          (uid) => uid !== user.uid
        );
        if (recipients.length > 0) {
          // discussionMention names the reply that actually did the
          // mentioning so the server can verify it (author, recipients,
          // and the Jr Staff overlay's removal fence) before delivering
          // anything - see the field's own comment on NotifyOptions and
          // loadVerifiedDiscussionReplyMention's in notificationAuthority.mjs.
          // Both ids are required now (PR #807 round 8, Codex P1-b): this
          // is the exact reply just saved above, so both are already in
          // hand. Not a lane selector: this send stays the ordinary
          // unscoped 'mention' kind, scoped to the people this reply
          // actually named, never the whole board.
          await notifyUsers(recipients, {
            kind: 'mention',
            title: `${realUserDoc ? teacherDisplayName(realUserDoc) : user.displayName || 'Someone'} mentioned you in a staff announcement`,
            body: `${parentTitle}: ${plainMessageText(trimmed).slice(0, 120)}`,
            link: `/messages#board-${discussionId}`,
          }, { discussionMention: { discussionId, replyId: replyRef.id } });
        }
      }
    } catch {
      // best-effort
    }
  };
}

export function newDiscussionId(): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'discussions')).id;
}

export function newDiscussionReplyId(discussionId: string): string {
  if (!db) throw new Error('Firestore not initialized.');
  return doc(collection(db, 'discussions', discussionId, 'replies')).id;
}

export async function deleteDiscussion(id: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await deleteDoc(doc(db, 'discussions', id));
}

/** Edit a post's title/body. Author (or Director) only, enforced by rules. */
export async function editDiscussion(
  id: string,
  fields: { title: string; body: string }
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const title = fields.title.trim();
  if (!title) throw new Error('Title is required.');
  await updateDoc(doc(db, 'discussions', id), {
    title,
    body: fields.body.trim(),
    editedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Edit a reply's text. Author (or Director) only, enforced by rules. */
export async function editReply(
  discussionId: string,
  replyId: string,
  text: string
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Empty reply.');
  await updateDoc(doc(db, 'discussions', discussionId, 'replies', replyId), {
    text: trimmed,
    editedAt: serverTimestamp(),
  });
}
